import { z } from "zod";
import toast from "react-hot-toast";
import { vendorApi } from "../api/vendor.api";
import { getErrorFromCatch } from "../api/api";
import { IVendor, IVendorRequest } from "../model/IVendor";
import { msg } from "../msg";
import checkFormStateByZod, { IZodErrorsState } from "../utils/checkFormStateByZod";
import { useAccess } from "./useAccess";

export function useVendor() {
  // -- Check permissions
  const { getSettingAccess } = useAccess();

  // --
  // -- Data checking scheme
  // --
  const vendorRequestSchema = z.object({
    name: z
      .string()
      .min(1, msg('vendor-no-name')),
  } as Record<keyof IVendorRequest, any>);

  // --
  // -- Data Getting Methods
  // --
  const [tryToAddVendor] = vendorApi.useAddVendorMutation();
  const [tryToEditVendor] = vendorApi.useEditVendorMutation();
  const [tryToDeleteVendor] = vendorApi.useDeleteVendorMutation();

  // --
  // -- Returns errors state - has errors
  // -- null - OK
  // --
  const addVendor = async (vendorState: IVendorRequest): Promise<IZodErrorsState<IVendorRequest> | null> => {
    // -- Check permissions
    if (!getSettingAccess('add-vendor')) {
      toast.error(msg('not-permissions'));
      return { name: msg('not-permissions') };
    }

    // 1. Check data
    const errs = checkFormStateByZod(vendorRequestSchema, vendorState);
    if (errs && Object.values(errs)?.length) return errs;

    // 2. Send data
    try {
      await tryToAddVendor(vendorState).unwrap();
      return null;
    } catch (err) {
      toast.error(`${msg('vendor-create-failed')} \n${getErrorFromCatch(err)}`);
      return { name: getErrorFromCatch(err) };
    }
  };

  // --
  // -- Returns errors state - has errors
  // -- null - OK
  // --
  const editVendor = async (id: IVendor['id'], vendorState: IVendorRequest): Promise<IZodErrorsState<IVendorRequest> | null> => {
    // -- Check permissions
    if (!getSettingAccess('edit-vendor')) {
      toast.error(msg('not-permissions'));
      return { name: msg('not-permissions') };
    }

    // 1. Check data
    const errs = checkFormStateByZod(vendorRequestSchema, vendorState);
    if (errs && Object.values(errs)?.length) return errs;

    // 2. Send data
    try {
      await tryToEditVendor({ id, ...vendorState }).unwrap();
      return null;
    } catch (err) {
      toast.error(`${msg('vendor-edit-failed')} \n${getErrorFromCatch(err)}`);
      return { name: getErrorFromCatch(err) };
    }
  };

  // --
  // -- Returns string = API error
  // -- null - OK
  // --
  const deleteVendor = async (id: IVendor['id']): Promise<string | null> => {
    // -- Check permissions
    if (!getSettingAccess('delete-vendor')) {
      toast.error(msg('not-permissions'));
      return msg('not-permissions');
    }

    // 1. Check data
    if (!id) return msg('not-enough-data');

    // 2. Send data
    try {
      await tryToDeleteVendor(id).unwrap();
      return null;
    } catch (err) {
      toast.error(`${msg('vendor-delete-failed')} \n${getErrorFromCatch(err)}`);
      return getErrorFromCatch(err);
    }
  };

  return {
    addVendor,
    editVendor,
    deleteVendor,
  };
}
